import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';

const HOT_ZONE = 74; // px from the very top of the viewport that reveals the links
const TUCK_DELAY = 200; // ms after the cursor leaves before links tuck away

const Navigation = () => {
  const location = useLocation();
  const [revealed, setRevealed] = useState(false); // desktop hover-reveal
  const [menuOpen, setMenuOpen] = useState(false); // touch/mobile toggle
  const hideTimer = useRef(null);

  const leftLinks = [
    { path: '/work', label: 'work' },
    { path: '/studio', label: 'studio' },
  ];
  const rightLinks = [
    { path: '/practice', label: 'practice' },
    { path: '/contact', label: 'contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // Desktop: reveal links while the cursor sits in the top hot-zone (and over
  // the nav), tuck them away shortly after the cursor leaves.
  useEffect(() => {
    const onMove = (e) => {
      if (e.clientY <= HOT_ZONE) {
        clearTimeout(hideTimer.current);
        setRevealed(true);
      } else {
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setRevealed(false), TUCK_DELAY);
      }
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      clearTimeout(hideTimer.current);
    };
  }, []);

  // Close the mobile menu on route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const revealStyle = {
    opacity: revealed ? 1 : 0,
    transform: revealed ? 'translateY(0)' : 'translateY(-6px)',
    pointerEvents: revealed ? 'auto' : 'none',
    transition: 'opacity 200ms ease, transform 200ms ease',
  };

  const linkClass = (path) => `nav-link${isActive(path) ? ' active' : ''}`;

  return (
    <nav className="nav-root fixed top-0 left-0 right-0 z-50">
      <style>{`
        /* Subtle off-white scrim so wordmark + links stay legible over a hero.
           mask fades the scrim AND the backdrop-blur out at the bottom so the
           nav blends seamlessly into the page with no hairline / hard edge. */
        .nav-root {
          background-image: linear-gradient(to bottom, rgba(248, 248, 247, 0.55), rgba(248, 248, 247, 0));
          -webkit-backdrop-filter: blur(6px);
          backdrop-filter: blur(6px);
          -webkit-mask-image: linear-gradient(to bottom, #000 70%, transparent 100%);
          mask-image: linear-gradient(to bottom, #000 70%, transparent 100%);
        }
        .nav-link {
          color: #3A3530;
          transition: color 250ms ease;
        }
        .nav-link:hover { color: #002F6C; }
        .nav-link.active { color: #002F6C; }

        /* Touch / no-hover devices: hide the inline link groups, show mobile actions */
        .nav-mobile-actions { display: none; }
        @media (hover: none), (max-width: 760px) {
          .nav-desktop-links { display: none !important; }
          .nav-mobile-actions { display: flex; }
        }
      `}</style>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-5">
        <div className="relative flex items-center justify-between min-h-[40px]">
          {/* LEFT links (desktop) */}
          <div className="nav-desktop-links flex items-center gap-12" style={revealStyle}>
            {leftLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${linkClass(link.path)} text-sm font-bold tracking-[0.15em] lowercase`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CENTER wordmark — always visible, absolutely centred */}
          <Link
            to="/"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center"
            aria-label="Studio 2+2 home"
          >
            <img src="/images/nametag.png" alt="Studio 2+2" className="h-7 w-auto" />
          </Link>

          {/* RIGHT links (desktop) */}
          <div className="nav-desktop-links flex items-center gap-12" style={revealStyle}>
            {rightLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${linkClass(link.path)} text-sm font-bold tracking-[0.15em] lowercase`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile top-bar actions — touch / mobile only (wordmark stays centred) */}
          <div className="nav-mobile-actions ml-auto items-center gap-4">
            <button
              type="button"
              className="flex items-center justify-center text-[#3A3530] hover:text-[#002F6C] transition-colors"
              aria-label="search projects"
              data-testid="nav-mobile-search"
              onClick={() => window.dispatchEvent(new CustomEvent('sandhan:open'))}
            >
              <Search size={21} />
            </button>
            <button
              type="button"
              className="flex items-center justify-center text-[#3A3530] hover:text-[#002F6C] transition-colors"
              aria-label={menuOpen ? 'close menu' : 'open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown panel */}
        {menuOpen && (
          <div className="flex flex-col items-center gap-5 pt-5 pb-2">
            {[...leftLinks, ...rightLinks].map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${linkClass(link.path)} text-sm font-bold tracking-[0.15em] lowercase`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
